import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getMe } from "../api/auth.api";
import { useAuthStore } from "../store/authStore";
import { useUploadStore } from "../store/uploadStore";
import { useExplorerStore } from "../store/explorerStore";
import { scanDroppedItems, scanFileList } from "../utils/fileScanner";
import { chunkFile } from "../utils/chunkFile";
import { softDeleteFile } from "../api/files.api";
import { resolveFolderTree } from "../api/folders.api";
import {
  presignSingleUpload,
  confirmSingleUpload,
  initMultipartUpload,
  getPartPresignedUrl,
  completeMultipartUpload,
  abortMultipartUpload,
  uploadPartToS3,
} from "../api/upload.api";
import type { ResolvedUploadFile, UploadPlan, UploadQueueItem } from "@titancloud/shared";

const MULTIPART_THRESHOLD = 5 * 1024 * 1024; // 5 MB
const CHUNK_SIZE_MB = 8;
const MAX_CONCURRENT = 3;

/** Refresh sidebar storage usage while large batches run (server-friendly interval). */
const QUOTA_REFRESH_INTERVAL_MS = 35_000;

// Throttle progress state updates to max 4/sec per file.
// Without this, 3 concurrent uploads × ~60 XHR events/sec = 180 Zustand
// updates/sec, each forcing a Map copy and a React re-render.
const PROGRESS_THROTTLE_MS = 250;

// Push queue items in batches so the initial addItems for 10,000 files
// doesn't cause one giant synchronous state update that freezes the tab.
const ADD_BATCH_SIZE = 100;

export function useUpload() {
  const { addItems } = useUploadStore();
  const { currentFolderId } = useExplorerStore();
  const qc = useQueryClient();

  const scanAndPlan = useCallback(
    async (dataTransfer: DataTransfer): Promise<UploadPlan> =>
      scanDroppedItems(dataTransfer),
    []
  );

  const planFromFileList = useCallback(
    (fileList: FileList): UploadPlan => scanFileList(fileList),
    []
  );

  const executeUpload = useCallback(
    async (resolved: ResolvedUploadFile[]) => {
      if (resolved.length === 0) return;

      const dirPaths = new Set<string>();
      for (const r of resolved) {
        const slash = r.relativePath.lastIndexOf("/");
        if (slash > 0) dirPaths.add(r.relativePath.slice(0, slash));
      }
      const hasNestedFolders = dirPaths.size > 0;

      // Placeholder folderId (currentFolderId) for nested items until the tree
      // resolves below. Queue items are shown to the user right away so the
      // upload widget doesn't sit blank while resolveFolderTree is in flight.
      const ts = Date.now();
      const queueItems: UploadQueueItem[] = resolved.map((r, i) => ({
        id: `upload-${ts}-${i}`,
        file: r.file,
        uploadAs: r.uploadAs,
        relativePath: r.relativePath,
        folderId: currentFolderId,
        status: "pending",
        progress: 0,
      }));

      // Batch add — yields between chunks so React can paint between each batch
      for (let i = 0; i < queueItems.length; i += ADD_BATCH_SIZE) {
        addItems(queueItems.slice(i, i + ADD_BATCH_SIZE));
        await new Promise<void>((r) => setTimeout(r, 0));
      }

      // Recreate the dropped folder structure: find-or-create every directory
      // referenced by a nested relativePath, then patch each affected item's
      // folderId in place — before any of them actually start uploading.
      if (hasNestedFolders) {
        const folderIdByDir = await resolveFolderTree(currentFolderId, [...dirPaths]);
        for (let i = 0; i < resolved.length; i++) {
          const slash = resolved[i]!.relativePath.lastIndexOf("/");
          if (slash <= 0) continue;
          const dir = resolved[i]!.relativePath.slice(0, slash);
          const folderId = folderIdByDir[dir] ?? currentFolderId;
          queueItems[i]!.folderId = folderId;
          useUploadStore.getState().updateItem(queueItems[i]!.id, { folderId });
        }
      }

      // Per-file throttle map — lives here in closure, NOT in React state
      const lastProgressAt = new Map<string, number>();

      const throttledProgress = (id: string, progress: number) => {
        const now = Date.now();
        if ((now - (lastProgressAt.get(id) ?? 0)) >= PROGRESS_THROTTLE_MS) {
          lastProgressAt.set(id, now);
          useUploadStore.getState().updateItem(id, { progress });
        }
      };

      const processNext = async (
        item: UploadQueueItem,
        replaceId?: string
      ): Promise<void> => {
        useUploadStore.getState().updateItem(item.id, { status: "uploading" });
        try {
          if (replaceId) await softDeleteFile(replaceId).catch(() => {});
          if (item.file.size < MULTIPART_THRESHOLD) {
            await uploadSingleFile(item, item.folderId, throttledProgress);
          } else {
            await uploadMultipartFile(item, item.folderId, throttledProgress);
          }
          useUploadStore.getState().updateItem(item.id, { status: "done", progress: 100 });
          // Show file in UI as soon as it's confirmed — don't wait for all uploads
          void qc.invalidateQueries({ queryKey: ["files", item.folderId] });
        } catch (err) {
          useUploadStore.getState().updateItem(item.id, {
            status: "error",
            error: err instanceof Error ? err.message : "Upload failed",
          });
        }
      };

      // Worker pool — each worker pulls from the shared queue until empty
      const workQueue = resolved.map((r, i) => ({
        item: queueItems[i]!,
        replaceId: r.replaceId,
      }));

      let quotaRefreshInFlight = false;
      const refreshUsageFromServer = async () => {
        if (quotaRefreshInFlight) return;
        quotaRefreshInFlight = true;
        try {
          const user = await getMe();
          useAuthStore.getState().setUser(user);
          qc.setQueryData(["auth", "me"], user);
        } catch {
          // ignore — e.g. transient network; uploads continue
        } finally {
          quotaRefreshInFlight = false;
        }
      };

      const quotaInterval = setInterval(() => {
        void refreshUsageFromServer();
      }, QUOTA_REFRESH_INTERVAL_MS);

      try {
        await Promise.all(
          Array.from({ length: Math.min(MAX_CONCURRENT, workQueue.length) }, () =>
            (async () => {
              while (workQueue.length > 0) {
                const entry = workQueue.shift();
                if (entry) await processNext(entry.item, entry.replaceId);
              }
            })()
          )
        );
      } finally {
        clearInterval(quotaInterval);
        await refreshUsageFromServer();
      }

      void qc.invalidateQueries({ queryKey: ["files", currentFolderId] });
      // Refresh folder listings: nested drops create sub-folders, and every upload changes the
      // item counts the cached folder tree shows.
      void qc.invalidateQueries({ queryKey: ["folders"] });
    },
    [addItems, currentFolderId, qc]
  );

  return { scanAndPlan, planFromFileList, executeUpload };
}

// ─── Upload helpers (plain async functions, not hooks) ─────────────────────

async function uploadSingleFile(
  item: UploadQueueItem,
  folderId: string | null,
  onProgress: (id: string, progress: number) => void
): Promise<void> {
  const { fileId, uploadUrl } = await presignSingleUpload({
    fileName: item.uploadAs,
    mimeType: item.file.type || "application/octet-stream",
    sizeBytes: item.file.size.toString(),
    folderId,
  });

  useUploadStore.getState().updateItem(item.id, { fileId });

  await uploadPartToS3(uploadUrl, item.file, (loaded, total) => {
    onProgress(item.id, Math.round((loaded / total) * 95));
  });

  useUploadStore.getState().updateItem(item.id, { status: "completing" });
  await confirmSingleUpload(fileId);
}

async function uploadMultipartFile(
  item: UploadQueueItem,
  folderId: string | null,
  onProgress: (id: string, progress: number) => void
): Promise<void> {
  const chunks = chunkFile(item.file, CHUNK_SIZE_MB);

  const { fileId, uploadId } = await initMultipartUpload({
    fileName: item.uploadAs,
    mimeType: item.file.type || "application/octet-stream",
    sizeBytes: item.file.size.toString(),
    totalParts: chunks.length,
    folderId,
  });

  useUploadStore.getState().updateItem(item.id, { fileId, uploadId });

  const completedParts: Array<{ partNumber: number; etag: string }> = [];
  let uploadedBytes = 0;

  try {
    for (const chunk of chunks) {
      const { uploadUrl } = await getPartPresignedUrl({
        fileId,
        uploadId,
        partNumber: chunk.partNumber,
        sizeBytes: chunk.size.toString(),
      });

      const etag = await uploadPartToS3(uploadUrl, chunk.blob, (loaded) => {
        onProgress(item.id, Math.round(((uploadedBytes + loaded) / item.file.size) * 95));
      });

      uploadedBytes += chunk.size;
      completedParts.push({ partNumber: chunk.partNumber, etag });
    }

    useUploadStore.getState().updateItem(item.id, { status: "completing" });
    await completeMultipartUpload({ fileId, uploadId, parts: completedParts });
  } catch (err) {
    await abortMultipartUpload(uploadId, fileId).catch(() => {});
    throw err;
  }
}
